import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { EmdBubbleMapChartComponent } from '../charts/emd-bubble-map-chart/emd-bubble-map-chart.component';
import { EmdDonutChartComponent } from '../charts/emd-donut-chart/emd-donut-chart.component';
import { EmdFilterService } from '../emd-filter.service';
import { EmdSelectionService } from '../emd-selection.service';
import {
  EMD_COLORS,
  EmdFilter,
  EmdSummaryData,
  EmdTrendBucket,
  SUMMARY_DEFAULT,
  WHOLESALER_COLORS,
  bucketKey,
  bucketSummaryTrend,
  deriveSummary,
  money,
  wholesalerColor,
} from '../emd-mock-data';

@Component({
    selector: 'emd-summary-tab',
    templateUrl: './emd-summary-tab.component.html',
    styleUrls: ['./emd-summary-tab.component.scss'],
    standalone: false
})
export class EmdSummaryTabComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(EmdBubbleMapChartComponent) bubbleChart?: EmdBubbleMapChartComponent;
  @ViewChild(EmdDonutChartComponent) donutChart?: EmdDonutChartComponent;

  readonly colors = EMD_COLORS;

  /** Latest derivation; every visual on the tab reads out of this. */
  data: EmdSummaryData = SUMMARY_DEFAULT;

  kpiCards = this.data.kpis;
  wholesalerLocations = this.data.wholesalerLocations;
  netSalesData = this.data.netSales;
  donutData = this.data.donut;
  valueGrid = this.data.valueGrid;
  valueGridMax = Math.max(...this.data.valueGrid.map((v) => v.r));

  ranges = ['R2W', 'R4W', 'R13W', 'R26W', 'R52W'];
  activeRange = 'R13W';
  // The Summary trend is fixed to weekly buckets (the W/M/Q/Y selector was
  // removed); trendBucket is still read by the chart's dateFormat and the
  // bucket-key mapping for period selection.
  trendBucket: EmdTrendBucket = 'W';

  netSalesToggle = 'units';
  donutToggle = 'units';

  donutSize = 260;
  // name is carried through for the hover tooltip; the ring itself only needs
  // value and color.
  donutChartData: any[] = [];
  donutTotalLabel = '';

  // ── Wholesaler Sales map ────────────────────────────────────────────────────

  mapBubbleData: { wholesaler: string; location: string; sales: number }[] = [];

  /** Every wholesaler, not just the ones currently plotted, so a badge cut can
   *  reintroduce any of them without the map's colours being rebuilt. */
  readonly mapColors = WHOLESALER_COLORS;

  // ── Sales Trend ─────────────────────────────────────────────────────────────
  // Bars take the right axis and lines the left, so total-sales QTY is the
  // right axis and $ the left.

  trendBarData: { period: string; value: number }[] = [];
  trendLineData: { period: string; series: string; value: number }[] = [];
  readonly trendBarColor = EMD_COLORS.primary;
  readonly trendLineColors = { 'SALES ($)': EMD_COLORS.primaryLt };

  /** Bucket keys of the bars whose weeks are staged — those bars stay solid. */
  trendSelectedPeriods: string[] = [];

  ngAfterViewInit(): void {
    this.refresh();
    // Re-apply any selection carried in from another tab, once the charts exist
    // to receive it.
    setTimeout(() => this.applySelection());
  }

  /** Weeks of history behind each range pill. */
  private static readonly RANGE_WEEKS: Record<string, number> = {
    R2W: 2, R4W: 4, R13W: 13, R26W: 26, R52W: 52,
  };

  onRangeChange(range: string): void {
    this.activeRange = range;
    this.refresh();
  }

  /**
   * Re-aggregate from the fact tables for the current badge filters and range,
   * and push the results into the charts. A confirmed cross-filter selection
   * lands in the Wholesaler badge, so it is already part of `filters.filter`.
   */
  private refresh(): void {
    const filter: EmdFilter = {
      ...this.filters.filter('Summary'),
      weeks: EmdSummaryTabComponent.RANGE_WEEKS[this.activeRange],
    };

    const d = deriveSummary(filter);
    this.data = d;
    this.kpiCards = d.kpis;
    this.wholesalerLocations = d.wholesalerLocations;
    this.netSalesData = d.netSales;
    this.donutData = d.donut;
    this.valueGrid = d.valueGrid;
    this.valueGridMax = Math.max(...d.valueGrid.map((v) => v.r));

    this.updateDonutView();
    this.updateTrendView();
    this.mapBubbleData = [...d.mapBubble];

    // The charts have just been rebuilt, so any active highlight is gone.
    setTimeout(() => this.applySelection());
  }

  /**
   * Rebuild what the donut ring and its centre show for the Units/$ toggle.
   * The slice split barely moves between the two modes (price is flat across
   * wholesalers), but the values and total swap between quantities and money.
   */
  private updateDonutView(): void {
    const inUsd = this.donutToggle === '$';
    this.donutChartData = this.donutData.map((s) => ({
      name: s.name,
      value: inUsd ? s.usd : s.value,
      color: wholesalerColor(s.name),
    }));
    const total = this.donutData.reduce((t, s) => t + (inUsd ? s.usd : s.value), 0);
    this.donutTotalLabel = inUsd ? money(total) : total.toLocaleString('en-US');
  }

  /**
   * Re-bucket the weekly derivation to the W/M/Q/Y pill and flatten to the
   * chart's shape here rather than in the chart, so the chart component stays
   * domain-agnostic.
   */
  private updateTrendView(): void {
    const { bar, line } = bucketSummaryTrend(
      this.data.trendBar,
      this.data.trendLine,
      this.trendBucket
    );
    this.trendBarData = bar.map((x) => ({ period: x.period, value: x.sales_qty }));
    this.trendLineData = line.map((x) => ({
      period: x.period,
      series: x.metric,
      value: x.sales_usd,
    }));
  }

  /**
   * A bar is one bucket; stage its member WEEKS, so the eventual period cut is
   * week-grained and applies identically on every tab whatever this chart's
   * current bucket is. Toggling works per bar because stage() toggles each
   * member week the same way.
   */
  onTrendBarClick(e: { period: string }): void {
    const weeks = this.data.trendBar
      .map((x) => x.period)
      .filter((p) => bucketKey(p, this.trendBucket) === e.period);
    for (const week of weeks) {
      this.selection.stage('period', week, 'summary-trend');
    }
  }

  onDonutToggleChange(value: string): void {
    this.donutToggle = value;
    this.updateDonutView();
    // New ring data redraws the donut, dropping any active highlight.
    setTimeout(() => this.applySelection());
  }

  // ── Cross-filtering ─────────────────────────────────────────────────────────
  // Every wholesaler-scoped visual on this tab — the map, the locations legend,
  // the donut and its legend, and the net sales list — publishes to and reads
  // from the shared selection, so clicking any one of them highlights the
  // matching data in all the others and dims the rest. Ctrl/Cmd-click extends
  // the selection, matching Power BI.

  private readonly subs: Subscription[] = [];

  constructor(
    public selection: EmdSelectionService,
    private filters: EmdFilterService
  ) {}

  ngOnInit(): void {
    this.subs.push(this.selection.changes.subscribe(() => this.applySelection()));
    this.subs.push(
      this.filters.changes.subscribe((tab) => {
        if (tab === 'Summary') { this.refresh(); }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  /**
   * Push the selection into the two D3 charts. The HTML visuals bind to
   * isWholesalerDimmed directly, so they need nothing here.
   */
  private applySelection(): void {
    // Highlight against the EFFECTIVE selection (pending while the user is still
    // choosing, else committed), so a staged selection previews live and the
    // map/donut fade rather than hide the rest.
    const active = this.selection.effective('wholesaler');
    this.bubbleChart?.highlightSources(active);
    this.donutChart?.highlightKeys(active);

    // Staged weeks map back to this chart's current bucket keys.
    const weeks = this.selection.effective('period');
    this.trendSelectedPeriods = weeks.length
      ? [...new Set(weeks.map((w) => bucketKey(w, this.trendBucket)))]
      : [];
  }

  /**
   * Every click toggles the value in the pending selection (Qlik-style
   * accumulate). `origin` names the widget the click came from so its header
   * shows the confirm/cancel toolbar.
   */
  selectWholesaler(name: string, origin: string): void {
    this.selection.stage('wholesaler', name, origin);
  }

  isWholesalerSelected(name: string): boolean {
    return this.selection.isSelected('wholesaler', name);
  }

  isWholesalerDimmed(name: string): boolean {
    return this.selection.isDimmed('wholesaler', name);
  }

  /** Hover preview — suppressed once a selection is being made or is applied. */
  highlightWholesaler(name: string | null): void {
    if (this.selection.hasAny('wholesaler')) return;
    this.bubbleChart?.highlightSource(name);
    this.donutChart?.highlightKeys(name ? [name] : null);
  }

  onBubbleClick(e: { sourceName: string; ctrlKey: boolean }): void {
    this.selection.stage('wholesaler', e.sourceName, 'summary-map');
  }

  onDonutSegmentClick(e: { name: string; ctrlKey: boolean }): void {
    this.selection.stage('wholesaler', e.name, 'summary-donut');
  }

  trackByName(_index: number, item: { name: string }): string {
    return item.name;
  }

  colorFor(name: string): string {
    return wholesalerColor(name);
  }

  formatNetSales(row: { units: number; usd: number }): string {
    return this.netSalesToggle === 'units'
      ? row.units.toLocaleString('en-US')
      : money(row.usd);
  }

  donutValueLabel(d: { value: number; usd: number }): string {
    return this.donutToggle === '$' ? money(d.usd) : d.value.toLocaleString('en-US');
  }

  donutPct(d: { pct: number; pctUsd: number }): number {
    return this.donutToggle === '$' ? d.pctUsd : d.pct;
  }
}
